// Mirrors backend com.kervolt.oms.rules.core.dto.{RuleDTO,RuleRequest,RuleKindDTO} and
// com.kervolt.oms.rules.bank.dto.BankRuleDtos 1:1 (FW-4A).

export type RuleFieldType = 'TEXT' | 'NUMBER' | 'ENUM' | 'REFERENCE';
export type RuleMatchMode = 'ALL' | 'ANY' | 'NONE';
export type RuleOperator =
  | 'CONTAINS' | 'NOT_CONTAINS' | 'EQUALS' | 'NOT_EQUALS' | 'STARTS_WITH' | 'ENDS_WITH'
  | 'EQ' | 'NE' | 'GT' | 'LT' | 'GE' | 'LE' | 'BETWEEN'
  | 'IS' | 'IS_NOT';

export const OPERATOR_LABEL: Record<RuleOperator, string> = {
  CONTAINS: 'contains', NOT_CONTAINS: 'does not contain', EQUALS: 'equals', NOT_EQUALS: 'does not equal',
  STARTS_WITH: 'starts with', ENDS_WITH: 'ends with',
  EQ: '=', NE: '≠', GT: '>', LT: '<', GE: '≥', LE: '≤', BETWEEN: 'between',
  IS: 'is', IS_NOT: 'is not',
};

export const MATCH_MODE_LABEL: Record<RuleMatchMode, string> = {
  ALL: 'Every condition matches', ANY: 'Any condition matches', NONE: 'No condition matches',
};

export interface RuleField {
  code: string;
  label: string;
  type: RuleFieldType;
  enumOptions: string[];
}

export interface RuleKindInfo {
  kind: string;
  subjectType?: string | null;
  fields: RuleField[];
  operators: Record<RuleFieldType, RuleOperator[]>;
  actions: string[];
  minConditions: number;
  maxConditions: number;
}

export interface BankRuleCondition {
  field: string;
  operator: RuleOperator;
  valueText?: string | null;
  valueNumber?: number | null;
  valueNumberTo?: number | null;
  sortOrder?: number;
}

export interface BankRuleAction {
  actionType: string;
  accountId?: string | null;
  segmentId?: string | null;
  departmentId?: string | null;
  customerId?: string | null;
  supplierId?: string | null;
  taxCodeId?: string | null;
  itemId?: string | null;
  descriptionTemplate?: string | null;
}

export interface BankRule {
  id: string;
  institutionId: string;
  ruleKind: string;
  scopeId?: string | null;
  name: string;
  priority: number;
  matchMode: RuleMatchMode;
  active: boolean;
  hitCount: number;
  lastHitAt?: string | null;
  createdAt?: string | null;
  conditions: BankRuleCondition[];
  action?: BankRuleAction | null;
}

export interface BankRuleRequest {
  name: string;
  scopeId?: string | null;
  matchMode: RuleMatchMode;
  priority?: number | null;
  conditions: BankRuleCondition[];
  action: BankRuleAction;
}

export interface BankRulePreviewRow {
  lineId: string;
  lineDate: string;
  description?: string | null;
  reference?: string | null;
  amount: number;
  direction: 'IN' | 'OUT';
  ruleId: string;
  ruleName: string;
  actionType: string;
  accountId?: string | null;
  accountLabel?: string | null;
  segmentId?: string | null;
  departmentId?: string | null;
  postedDescription?: string | null;
  warnings: string[];
  /** FW-4B: the party the action targets (customer / supplier name); null for post-to-account. */
  targetLabel?: string | null;
}

export interface BankRulePreview {
  reconciliationId: string;
  unmatchedLines: number;
  matchedByRule: number;
  rows: BankRulePreviewRow[];
}

export interface BankRuleApplyItem { lineId: string; ruleId: string; }

export interface BankRuleApplyItemResult {
  lineId: string;
  ruleId: string;
  status: 'APPLIED' | 'FAILED';
  message?: string | null;
  journalEntryId?: string | null;
}

export interface BankRuleApplyResult {
  requested: number;
  applied: number;
  failed: number;
  results: BankRuleApplyItemResult[];
}

export interface BankRuleTestLine {
  lineId: string;
  reconciliationId: string;
  lineDate: string;
  description?: string | null;
  reference?: string | null;
  amount: number;
  direction: 'IN' | 'OUT';
}

export interface BankRuleTestResult { count: number; truncated: boolean; lines: BankRuleTestLine[]; }

// ---- FW-4B ----
export type BankRuleActionType = 'POST_TO_ACCOUNT' | 'CUSTOMER_RECEIPT' | 'SUPPLIER_ADVANCE' | 'BILL_AND_PAY' | 'CASH_SALE';

export const ACTION_LABEL: Record<string, string> = {
  POST_TO_ACCOUNT: 'Post to account',
  CUSTOMER_RECEIPT: 'Customer receipt',
  SUPPLIER_ADVANCE: 'Supplier advance',
  BILL_AND_PAY: 'Bill and pay',
  CASH_SALE: 'Cash sale',
};

export const ACTION_HELP: Record<string, string> = {
  POST_TO_ACCOUNT: 'Books the line straight to an account as a bank adjustment (deposits and withdrawals).',
  CUSTOMER_RECEIPT: 'Deposits only. Records an on-account receipt from the customer; apply it to invoices later.',
  SUPPLIER_ADVANCE: 'Withdrawals only. Pays an advance to the supplier; allocate it to bills later.',
  BILL_AND_PAY: 'Withdrawals only. Creates a service bill (the amount includes tax) and pays it from this bank account.',
  CASH_SALE: 'Deposits only. Records a cash sale (the amount includes tax) and its receipt.',
};

/** Which fields each action asks for in the rule editor. */
export const ACTION_FIELDS: Record<string, { account?: 'expense' | 'revenue' | 'post'; customer?: 'required' | 'optional'; supplier?: boolean; item?: boolean; taxSide?: 'INPUT' | 'OUTPUT'; segment?: boolean; department?: boolean; template?: boolean }> = {
  POST_TO_ACCOUNT: { account: 'post', segment: true, department: true, template: true },
  CUSTOMER_RECEIPT: { customer: 'required' },
  SUPPLIER_ADVANCE: { supplier: true },
  BILL_AND_PAY: { supplier: true, account: 'expense', taxSide: 'INPUT', segment: true, template: true },
  CASH_SALE: { customer: 'optional', item: true, account: 'revenue', taxSide: 'OUTPUT', segment: true, template: true },
};

export interface BankRuleHitStat { ruleId: string; month: string; hits: number; }

export interface BankRuleApplication {
  id: string;
  statementLineId: string;
  ruleId: string;
  ruleName?: string | null;
  actionType: string;
  status: 'APPLIED' | 'FAILED' | 'UNDONE';
  journalEntryId?: string | null;
  documentType?: string | null;
  documentId?: string | null;
  message?: string | null;
  appliedAt?: string | null;
}
// ---- end FW-4B ----
